import React, { useState, useRef, useContext, useEffect } from 'react'
import { Auth } from 'aws-amplify'
import { Text, Input } from '@/style-guide'
import { Divider, Button, Form } from 'antd'
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth'
import { AuthContext } from '../AuthContext'
import { setItemStorage } from '@/utils/storage'
import common from '@/utils/common'
const LOGIN_ERRORS = {
  USER_NOT_FOUND_COGNITO: 'UserNotFoundException',
  USER_NOT_CONFIRMED: 'UserNotConfirmedException',
  USER_NOT_FOUND_TRIPSHA: 'userNotFound',
  USER_NOT_AUTHORIZED: 'NotAuthorizedException',
}

export const LoginForm = ({ showGoogle = true, onShowOtherLogin }) => {
  const { setStep, setPayload, onSuccess } = useContext(AuthContext)
  const [loginForm] = Form.useForm()
  const [newPassForm] = Form.useForm()
  const [isLoading, setLoading] = useState(false)
  const userAttributesRef = useRef({})
  const [showNewPasswordForm, setNewPasswordForm] = useState(false)
  const [cognitoUser, setCognitoUser] = useState({})
  const password_submit = async (values) => {
    cognitoUser.completeNewPasswordChallenge(values.new_password, null, {
      onSuccess: onSuccess,
      onFailure: () => {
        common.errorToast(
          "Oh no! It appears that there was an issue with resetting your password. We apologize for the inconvenience. Please try again later, and if the problem persists, kindly contact our support team for further assistance. They'll be ready to assist you in resolving this matter.",
        )
      },
    })
  }
  const submit = async (values) => {
    const email = values.email.toLowerCase()
    try {
      setLoading(true)
      const data = await Auth.signIn(email, values.password)
      if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setCognitoUser(data)
        setNewPasswordForm(true)
        return
      }
      userAttributesRef.current = data.attributes
      onSuccess()
    } catch (error) {
      if (error.code === LOGIN_ERRORS.USER_NOT_CONFIRMED) {
        setPayload({ email: email, password: values.password })
        setStep('confirm-email')
      } else if (error.code === LOGIN_ERRORS.USER_NOT_AUTHORIZED) {
        return common.errorToast(error.message)
      } else if (error.code === LOGIN_ERRORS.USER_NOT_FOUND_COGNITO) {
        return common.errorToast(error.message)
      }
      if (error?.response?.data?.message?.indexOf('Signature expired') > -1) {
        const signOutUrl = `${process.env.REACT_APP_HOST_PATH}/logout`; // Or whichever URL you configured in Cognito's Allowed Sign-Out URLs
        await Auth.signOut({
            // --- FIX IS HERE ---
            global: true, // Optional: Sign out from all devices
            redirectUri: signOutUrl // CRITICAL: Tells Cognito where to send the user
            // --- END OF FIX ---
        });
        return common.errorToast(
          'Login failed. Please set your time zone to update automatically and try login again.',
        )
      }

      if (error.response) {
        const {
          data: { result },
        } = error.response
        if (result?.type === LOGIN_ERRORS.USER_NOT_FOUND_TRIPSHA) {
          setStep('login')
          return common.errorToast(
            'We’ve encountered an unexpected error. Please try again and contact us at hello@tripsha.com if the error persists.',
          )
        }
      }
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    loginForm.setFieldsValue({ email: '', password: '' })
  }, [])
  return showNewPasswordForm ? (
    <Form
      form={newPassForm}
      onFinish={password_submit}
      initialValues={{
        new_password: '',
        confirm_new_password: '',
      }}
      scrollToFirstError
    >
      <Form.Item
        name="new_password"
        rules={[{ required: true, message: 'Please enter your new password' }]}
      >
        <Input label="New Password" htmlType="password" />
      </Form.Item>
      <Form.Item
        name="confirm_new_password"
        rules={[
          { required: true, message: 'Please confirm your new password' },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('new_password') === value) {
                return Promise.resolve()
              }
              return Promise.reject(
                new Error('The two passwords that you entered do not match!'),
              )
            },
          }),
        ]}
      >
        <Input label="Confirm New Password" htmlType="password" />
      </Form.Item>
      <Form.Item>
        <Button
          block
          htmlType="submit"
          loading={isLoading}
          style={{ height: '60px' }}
          size="large"
          type="primary"
        >
          Submit
        </Button>
      </Form.Item>
      <Text className="link-text" onClick={() => setNewPasswordForm(false)}>
        Back to login
      </Text>
    </Form>
  ) : (
    <Form
      form={loginForm}
      onFinish={submit}
      initialValues={{ email: '', password: '' }}
      scrollToFirstError
    >
      <Form.Item
        name="email"
        rules={[{ required: true, message: 'Please input your email.' }]}
      >
        <Input name="email" label="Email address" />
      </Form.Item>
      <Form.Item
        name="password"
        rules={[{ required: true, message: 'Please input your password.' }]}
      >
        <Input name="password" label="Password" htmlType="password" />
      </Form.Item>
      <Form.Item>
        <Button
          block
          htmlType="submit"
          size="large"
          type="primary"
          label="Login"
          className="xl-btn"
          loading={isLoading}
        >
          Login
        </Button>
      </Form.Item>
      {showGoogle && (
        <>
          <Divider>OR</Divider>
          <Button
            block
            className="xl-btn"
            size="large"
            onClick={() => {
              setItemStorage('redirectUrl', window.location.pathname)
              Auth.federatedSignIn({
                provider: 'Google',
              })
            }}
          >
            <span className="google" />
            Login using Google
          </Button>
        </>
      )}
      <div className="footer">
        <Text size={16}>
          <Text
            className="link-text"
            onClick={() => setStep('forgot-password')}
          >
            Forgot password?
          </Text>
        </Text>
        <Text size={16}>
          Don't have an account?
          <Text className="link-text" onClick={() => setStep('signup')}>
            {' '}
            Sign up
          </Text>
        </Text>
        {onShowOtherLogin && (
          <Text size={16} style={{ marginTop: 16, display: 'block' }}>
            <Text className="link-text" onClick={onShowOtherLogin}>
              Use other login options &gt;
            </Text>
          </Text>
        )}
      </div>
    </Form>
  )
}
